import { getDbSupabase } from './supabase';

export type DashboardSalesMonth = {
  month: number;
  this_year: number;
  last_year: number;
};

export type DashboardMonthlySales = {
  year: number;
  last_year: number;
  months: DashboardSalesMonth[];
  meta?: {
    salesperson_scope?: string;
    salesperson_codes?: string[];
    proxy_rev?: string;
    elevated?: boolean;
    own_codes_count?: number;
    want_all?: boolean;
  };
};

function num(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Fetch YoY monthly sales from customer_ledger via account-sales proxy.
 * - Omit `codes` + `all=1` for elevated company-wide (User=All).
 * - Pass `codes: []` for empty chart when elevated views a user with no codes.
 * - Pass `codes: ['SP1', …]` to filter.
 * Non-elevated users are always scoped by the proxy to their own codes (codes ignored).
 */
export async function fetchDashboardMonthlySales(
  codes?: string[] | null,
  opts?: { requestAll?: boolean },
): Promise<DashboardMonthlySales> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  try {
    const { data } = await getDbSupabase('crm').auth.getSession();
    const token = data.session?.access_token?.trim();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
      // Some hosts / old SWs strip Authorization — mirror DEM proxy fallback.
      headers['X-Account-Sales-Access-Token'] = token;
    }
  } catch {
    // proxy rejects unauthenticated
  }

  const params = new URLSearchParams({ resource: 'dashboard_sales' });
  if (opts?.requestAll) {
    params.set('all', '1');
  }
  if (codes !== null && codes !== undefined) {
    params.set('codes', codes.map((c) => c.trim()).filter(Boolean).join(','));
  }

  const response = await fetch(`/account-sales-api?${params.toString()}`, { headers });
  const json = (await response.json().catch(() => null)) as
    | (Record<string, unknown> & { error?: string; detail?: string; hint?: string })
    | null;

  if (!response.ok) {
    const detail =
      typeof json?.detail === 'string' && json.detail.trim() ? ` (${json.detail.trim()})` : '';
    const hint =
      typeof json?.hint === 'string' && json.hint.trim() ? ` ${json.hint.trim()}` : '';
    throw new Error(
      (json?.error || `Failed to load dashboard sales (${response.status}).`) + detail + hint,
    );
  }
  if (!json || typeof json !== 'object') {
    throw new Error('Invalid dashboard sales response.');
  }

  // Proxy / PostgREST may leave jsonb as a string in rare cases.
  let payload: Record<string, unknown> = json;
  if (typeof (json as { data?: unknown }).data === 'string') {
    try {
      const inner = JSON.parse((json as { data: string }).data) as unknown;
      if (inner && typeof inner === 'object') payload = inner as Record<string, unknown>;
    } catch {
      /* keep payload */
    }
  }

  const monthsRaw = Array.isArray(payload.months) ? payload.months : [];
  const months: DashboardSalesMonth[] = [];
  for (let m = 1; m <= 12; m++) {
    const row = monthsRaw.find(
      (r) => r && typeof r === 'object' && Number((r as Record<string, unknown>).month) === m,
    ) as Record<string, unknown> | undefined;
    months.push({
      month: m,
      this_year: num(row?.this_year),
      last_year: num(row?.last_year),
    });
  }

  const metaRaw =
    payload.meta && typeof payload.meta === 'object'
      ? (payload.meta as Record<string, unknown>)
      : {};
  const metaCodes = Array.isArray(metaRaw.salesperson_codes)
    ? metaRaw.salesperson_codes.map((c) => String(c).trim()).filter(Boolean)
    : undefined;

  return {
    year: num(payload.year) || new Date().getFullYear(),
    last_year: num(payload.last_year) || new Date().getFullYear() - 1,
    months,
    meta: {
      salesperson_scope:
        typeof metaRaw.salesperson_scope === 'string' ? metaRaw.salesperson_scope : undefined,
      salesperson_codes: metaCodes,
      proxy_rev: typeof metaRaw.proxy_rev === 'string' ? metaRaw.proxy_rev : undefined,
      elevated: typeof metaRaw.elevated === 'boolean' ? metaRaw.elevated : undefined,
      own_codes_count:
        typeof metaRaw.own_codes_count === 'number' ? metaRaw.own_codes_count : undefined,
      want_all: typeof metaRaw.want_all === 'boolean' ? metaRaw.want_all : undefined,
    },
  };
}

export function formatSalesAmountShort(value: number): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}k`;
  return `${sign}$${abs.toFixed(0)}`;
}

export function formatSalesAmountFull(value: number): string {
  return new Intl.NumberFormat('en-SG', {
    style: 'currency',
    currency: 'SGD',
    maximumFractionDigits: 0,
  }).format(value);
}

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function monthShortLabel(month: number): string {
  return MONTH_LABELS[Math.max(1, Math.min(12, month)) - 1] ?? String(month);
}
